import { Permissions } from '@/constants/permissionEnums'
import { useAuthStore } from '@/store/auth'
import { PermissionIdentifier } from '@/types/auth'
import { computed } from 'vue'

export const usePermissions = () => {
  const authStore = useAuthStore()

  // Transform permissions into a Set for O(1) lookups
  const permissionSet = computed(() => {
    const set = new Set<string>()
    if (authStore.user) {
      for (const permission of authStore.user.permissions) {
        if (permission) {
          set.add(permission.codename)
        }
      }
    }
    return set
  })

  const hasPermission = (identifiers?: PermissionIdentifier | PermissionIdentifier[]) => {
    // If no identifiers are provided, the action is allowed by default
    if (!identifiers) return true

    // Convert identifiers to an array if it's not already
    const identifiersArray = Array.isArray(identifiers) ? identifiers : [identifiers]

    // Check if any of the identifiers are in the permission set
    return identifiersArray.some((identifier) => permissionSet.value.has(identifier))
  }

  // Precompute all permissions for easy access
  const permissions = computed(() => {
    return {
      matchingEngine: {
        view: permissionSet.value.has(Permissions.CAN_VIEW_MATCHING_ENGINE),
        match: permissionSet.value.has(Permissions.CAN_MATCH_ENTITY_PRODUCT_TO_BASE_BOTTLE),
        unmatch: permissionSet.value.has(Permissions.CAN_UNMATCH_PRODUCT),
      },
      baseProducts: {
        view: permissionSet.value.has(Permissions.CAN_VIEW_BASE_PRODUCTS),
        merge: permissionSet.value.has(Permissions.CAN_MERGE_BASE_PRODUCTS),
        create: permissionSet.value.has(Permissions.CAN_CREATE_BASE_PRODUCTS),
        update: permissionSet.value.has(Permissions.CAN_UPDATE_BASE_PRODUCTS),
        viewAnalytics: permissionSet.value.has(Permissions.CAN_VIEW_BASE_PRODUCT_ANALYTICS),
      },
      entityProducts: {
        view: permissionSet.value.has(Permissions.CAN_VIEW_ENTITY_PRODUCTS),
        create: permissionSet.value.has(Permissions.CAN_UPDATE_ENTITY_PRODUCT),
        update: permissionSet.value.has(Permissions.CAN_UPDATE_ENTITY_PRODUCT),
        addVariant: permissionSet.value.has(Permissions.CAN_ADD_VARIANT_TO_ENTITY_PRODUCT),
        delete: permissionSet.value.has(Permissions.CAN_DELETE_ENTITY_PRODUCT),
        blacklist: permissionSet.value.has(Permissions.CAN_BLACKLIST_ENTITY_PRODUCT),
        removeBlacklist: permissionSet.value.has(Permissions.CAN_REMOVE_BLACKLIST_ENTITY_PRODUCT),
      },
      preAccounts: {
        view: permissionSet.value.has(Permissions.CAN_VIEW_PRE_ACCOUNTS),
        create: permissionSet.value.has(Permissions.CAN_CREATE_PRE_ACCOUNT),
        update: permissionSet.value.has(Permissions.CAN_UPDATE_PRE_ACCOUNT),
        delete: permissionSet.value.has(Permissions.CAN_DELETE_PRE_ACCOUNT),
        search: permissionSet.value.has(Permissions.CAN_SEARCH_PRE_ACCOUNT),
        promote: permissionSet.value.has(Permissions.CAN_PROMOTE_PRE_ACCOUNT),
      },
      business: {
        jump: permissionSet.value.has(Permissions.CAN_JUMP_TO_BUSINESS),
      },
      retailerCheck: {
        view: permissionSet.value.has(Permissions.CAN_VIEW_ACCOUNT),
        activate: permissionSet.value.has(Permissions.CAN_ACTIVATE_ACCOUNT),
        deactivate: permissionSet.value.has(Permissions.CAN_DEACTIVATE_ACCOUNT),
      },
      podFiles: {
        view: permissionSet.value.has(Permissions.CAN_VIEW_POD_FILES),
      },
      reportedIssues: {
        view: permissionSet.value.has(Permissions.CAN_VIEW_REPORTED_ISSUES),
        update: permissionSet.value.has(Permissions.CAN_UPDATE_REPORTED_ISSUE),
      },
      barcodeResolution: {
        view: permissionSet.value.has(Permissions.CAN_VIEW_BARCODE_RESOLUTION),
        resolve: permissionSet.value.has(Permissions.CAN_RESOLVE_BARCODE),
      },
      blacklistedProducts: {
        view: permissionSet.value.has(Permissions.CAN_VIEW_BLACKLISTED_PRODUCTS),
      },
      mergeJobs: {
        view: permissionSet.value.has(Permissions.CAN_VIEW_MERGE_JOBS),
        revert: permissionSet.value.has(Permissions.CAN_REVERT_MERGE_JOBS),
      },
      overview: {
        view: permissionSet.value.has(Permissions.CAN_VIEW_OVERVIEW_ANALYTICS),
        viewMapChart: permissionSet.value.has(Permissions.CAN_VIEW_RETAILER_MAP_CHART),
      },
    }
  })

  return { hasPermission, permissions }
}
